// LightStar.js — "Light their star tonight."
// A paid act of tribute, open to anyone (like leaving a flower): for one
// night (or seven), their star burns brighter in the Galaxy of Memorials,
// carrying the dedication of the person who lit it. The free candle stays
// free and the free anniversary glow stays free — this is the deliberate,
// visible-in-the-sky gesture above them.
//
// Data: top-level `starGlows` collection —
//   { starId, memorialId, memorialSlug, litBy, message, tier,
//     litAt, expiresAt, sessionId, amount }
// The galaxy reads active glows (expiresAt > now) and amplifies those stars.

import React, { useState, useEffect, useRef } from 'react';
import {
  collection, addDoc, query, where, onSnapshot, Timestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { isInNativeApp } from '../platform';
import { iapAvailable, iapLogIn, purchaseIap } from '../iap';
import { events } from '../analytics';

const GOLD = '#e5c883';

const TIERS = [
  { id: 'night', label: 'Tonight', nights: 1, price: '$4.99', blurb: 'Their star burns brighter for 24 hours' },
  { id: 'week', label: 'Seven Nights', nights: 7, price: '$12.99', blurb: 'A week of light, for the hardest weeks' },
];

// --- Rotating dedications ---------------------------------------------------
// Every person who lit the star deserves to be seen, not just the most recent.
// Rather than stacking them (which crowds the card and buries the message),
// the dedications take turns: one at a time, crossfading every few seconds,
// like candles speaking in turn. Small gold dots show how many lights burn and
// let someone jump to a particular one; tapping pauses the rotation so a
// dedication can be read in peace.

// When someone lights a star without leaving words, the star still says
// something on their behalf.
const DEFAULT_DEDICATION = 'Remembering you tonight.';

function RotatingDedications({ dedications }) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(false);
  const count = dedications.length;

  // Keep the index valid as glows arrive or expire.
  useEffect(() => {
    setIndex((i) => (i >= count ? 0 : i));
  }, [count]);

  useEffect(() => {
    if (count < 2 || paused) return undefined;
    const HOLD = 5200;   // time a dedication stays fully visible
    const FADE = 600;    // crossfade duration (matches the CSS transition)
    const fadeOut = setTimeout(() => setVisible(false), HOLD);
    const advance = setTimeout(() => {
      setIndex((i) => (i + 1) % count);
      setVisible(true);
    }, HOLD + FADE);
    return () => { clearTimeout(fadeOut); clearTimeout(advance); };
  }, [index, count, paused]);

  // Tapping a dot shows that dedication and pauses; rotation resumes later.
  useEffect(() => {
    if (!paused) return undefined;
    const t = setTimeout(() => setPaused(false), 20000);
    return () => clearTimeout(t);
  }, [paused, index]);

  const current = dedications[Math.min(index, count - 1)];
  if (!current) return null;

  return (
    <div className="mt-4 max-w-md mx-auto">
      <div
        className="flex items-center justify-center px-2"
        style={{ minHeight: '5rem' }}
      >
        <p
          className="text-lg md:text-xl font-extralight"
          style={{
            color: '#f5efe2',
            letterSpacing: '0.02em',
            lineHeight: 1.5,
            opacity: visible ? 1 : 0,
            transition: 'opacity 600ms ease',
          }}
        >
          {current.message ? (
            <>
              &ldquo;{current.message}&rdquo;
              <span className="block mt-2 text-sm" style={{ color: '#ffe9b8' }}>&mdash; {current.litBy}</span>
            </>
          ) : (
            <>
              &ldquo;{DEFAULT_DEDICATION}&rdquo;
              <span className="block mt-2 text-sm" style={{ color: '#ffe9b8' }}>&mdash; {current.litBy}</span>
            </>
          )}
        </p>
      </div>

      {count > 1 && (
        <div className="mt-3 flex items-center justify-center gap-1.5">
          {dedications.map((g, i) => (
            <button
              key={g.id}
              onClick={() => { setIndex(i); setVisible(true); setPaused(true); }}
              aria-label={`Dedication ${i + 1} of ${count}`}
              className="p-1 leading-none"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <span
                className="block rounded-full"
                style={{
                  width: i === index ? '7px' : '5px',
                  height: i === index ? '7px' : '5px',
                  background: i === index ? '#ffe2a8' : 'rgba(255,226,168,0.32)',
                  boxShadow: i === index ? '0 0 10px rgba(255,215,140,0.75)' : 'none',
                  transition: 'all 400ms ease',
                }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LightStar({ memorial, memorialId }) {
  const starId = memorial?.galaxyLocation?.starId;
  const starName = memorial?.galaxyLocation?.starName || `${memorial?.firstName}'s Star`;

  const [activeGlows, setActiveGlows] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [tier, setTier] = useState('night');
  const [litBy, setLitBy] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [justLit, setJustLit] = useState(false);
  const fulfilling = useRef(false);

  // Live active glows for this star
  useEffect(() => {
    if (!starId) return undefined;
    const q = query(
      collection(db, 'starGlows'),
      where('starId', '==', starId),
      where('expiresAt', '>', Timestamp.now())
    );
    const unsub = onSnapshot(
      q,
      (snap) => setActiveGlows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.warn('starGlows subscribe failed:', e)
    );
    return unsub;
  }, [starId]);

  // Prefill the lighter's name from their account
  useEffect(() => {
    const user = auth.currentUser;
    if (user?.displayName && !litBy) setLitBy(user.displayName);
    // eslint-disable-next-line
  }, [modalOpen]);

  // ── Fulfillment: returning from Stripe with ?glow_session_id ──────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('glow_session_id');
    if (!sessionId || fulfilling.current) return;
    fulfilling.current = true;

    const cleanUrl = () => {
      params.delete('glow_session_id');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    };

    const run = async () => {
      // Already written in this browser? Just celebrate again quietly.
      if (sessionStorage.getItem(`glowFulfilled_${sessionId}`)) {
        cleanUrl();
        return;
      }
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verifyGlowPayment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });
        const verified = await res.json();
        if (!verified?.success) { cleanUrl(); return; }

        const meta = verified.metadata || {};
        let pending = null;
        try { pending = JSON.parse(sessionStorage.getItem('pendingGlow') || 'null'); } catch (_) { pending = null; }

        const nights = (meta.tier || pending?.tier) === 'week' ? 7 : 1;
        const now = new Date();
        await addDoc(collection(db, 'starGlows'), {
          starId: meta.starId || pending?.starId || starId,
          memorialId: meta.memorialId || memorialId,
          memorialSlug: meta.memorialSlug || memorial?.slug || null,
          litBy: (pending?.litBy || meta.litBy || 'Someone who remembers').slice(0, 80),
          message: (pending?.message || meta.message || '').slice(0, 200),
          tier: nights === 7 ? 'week' : 'night',
          litAt: Timestamp.fromDate(now),
          expiresAt: Timestamp.fromDate(new Date(now.getTime() + nights * 24 * 60 * 60 * 1000)),
          sessionId,
          amount: verified.amount || null,
        });

        sessionStorage.setItem(`glowFulfilled_${sessionId}`, '1');
        sessionStorage.removeItem('pendingGlow');
        events.purchased(`light_star_${nights === 7 ? 'week' : 'night'}`, verified.amount || 0, 'stripe');
        setJustLit(true);
        cleanUrl();
      } catch (e) {
        console.error('Glow fulfillment failed:', e);
      }
    };
    run();
    // eslint-disable-next-line
  }, []);

  const checkout = async () => {
    setBusy(true);
    setError('');
    const price = tier === 'week' ? 12.99 : 4.99;
    events.beginCheckout(`light_star_${tier}`, price, iapAvailable() ? 'apple' : 'stripe');

    // iOS app: the purchase goes through Apple (App Store rule 3.1.1),
    // then the glow is written directly — same shape as the Stripe path.
    if (iapAvailable()) {
      try {
        if (auth.currentUser) await iapLogIn(auth.currentUser.uid);
        const result = await purchaseIap(tier === 'week' ? 'glowWeek' : 'glowNight');
        const txn =
          result?.transaction?.transactionIdentifier ||
          result?.productIdentifier ||
          'purchase';
        const nights = tier === 'week' ? 7 : 1;
        const now = new Date();
        await addDoc(collection(db, 'starGlows'), {
          starId,
          memorialId,
          memorialSlug: memorial?.slug || null,
          litBy: (litBy.trim() || 'Someone who remembers').slice(0, 80),
          message: message.trim().slice(0, 200),
          tier: nights === 7 ? 'week' : 'night',
          litAt: Timestamp.fromDate(now),
          expiresAt: Timestamp.fromDate(new Date(now.getTime() + nights * 24 * 60 * 60 * 1000)),
          sessionId: `iap_${txn}_${now.getTime()}`,
          amount: nights === 7 ? 12.99 : 4.99,
        });
        events.purchased(`light_star_${tier}`, price, 'apple');
        setModalOpen(false);
        setJustLit(true);
      } catch (e) {
        if (!e?.userCancelled) setError(e?.message || 'Something went wrong. Please try again.');
      } finally {
        setBusy(false);
      }
      return;
    }

    try {
      if (!window.Stripe) throw new Error('Stripe.js not loaded. Please refresh the page and try again.');
      const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
      if (!stripe) throw new Error('Stripe not initialized properly.');

      sessionStorage.setItem('pendingGlow', JSON.stringify({
        starId,
        tier,
        litBy: litBy.trim() || 'Someone who remembers',
        message: message.trim(),
      }));

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/createGlowSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          starId,
          memorialId,
          memorialSlug: memorial.slug,
          tier,
          litBy: litBy.trim() || 'Someone who remembers',
          message: message.trim(),
          customerEmail: auth.currentUser?.email || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to start checkout (HTTP ${res.status}).`);
      }
      const { sessionId } = await res.json();
      if (!sessionId) throw new Error('No session ID received from the server.');

      const { error: redirectError } = await stripe.redirectToCheckout({ sessionId });
      if (redirectError) {
        sessionStorage.removeItem('pendingGlow');
        throw new Error(redirectError.message);
      }
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  if (!starId) return null;
  // In the native app, purchases go through Apple In-App Purchase when the
  // plugin is present. Only an outdated app build (no plugin) withholds the
  // buying path (App Store guideline 3.1.1) — it always lives on the website.
  const hidePurchase = isInNativeApp() && !iapAvailable();

  const glowCount = activeGlows.length;


  const dedications = activeGlows
    .filter((g) => g.message || g.litBy)
    .sort((a, b) => (b.litAt?.seconds || 0) - (a.litAt?.seconds || 0))
    .slice(0, 24);

  return (
    <div id="light-star" className="mb-10">
      {/* When the star is lit: a night card showing who lights it */}
      {(glowCount > 0 || justLit) && (
        <div
          className="rounded-2xl p-6 md:p-8 mb-4 text-center relative overflow-hidden"
          style={{
            background: 'radial-gradient(ellipse at 50% 0%, #1a1530 0%, #05040d 80%)',
            border: '1px solid rgba(184,150,79,0.4)',
          }}
        >
          <div
            className="absolute left-1/2 -translate-x-1/2 top-5 w-2.5 h-2.5 rounded-full pointer-events-none"
            style={{
              background: '#fff3d0',
              boxShadow: '0 0 22px 9px rgba(255,220,150,0.8), 0 0 70px 30px rgba(255,205,120,0.3)',
              animation: 'soulStarPulse 3.2s ease-in-out infinite',
            }}
          />
          <p className="mt-8 text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#e5c883' }}>
            {starName} Is Lit
          </p>
          {dedications.length > 0 && <RotatingDedications dedications={dedications} />}
        </div>
      )}

      {/* The invitation — website only */}
      {!hidePurchase && (
      <div className="text-center">
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] uppercase transition-shadow hover:shadow-[0_0_28px_rgba(255,215,140,0.4)]"
          style={{
            letterSpacing: '0.22em',
            color: '#ffe2a8',
            border: '1px solid rgba(184,150,79,0.5)',
            background: 'linear-gradient(180deg, #171426, #0c0a18)',
          }}
        >
          ✦ Light their star tonight
        </button>
        <p className="mt-2 text-[11px] text-gray-400 font-light">
          For one night, their star burns brighter in the galaxy — with your dedication.
        </p>
      </div>
      )}

      {/* Modal */}
      {modalOpen && !hidePurchase && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ background: 'rgba(3,2,8,0.8)', backdropFilter: 'blur(6px)' }}
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setModalOpen(false); }}
        >
          <div
            className="w-full max-w-md rounded-2xl p-7 md:p-8 relative"
            style={{
              background: 'radial-gradient(ellipse at 50% 0%, #171426 0%, #0a0817 85%)',
              border: '1px solid rgba(184,150,79,0.45)',
            }}
          >
            <button
              onClick={() => !busy && setModalOpen(false)}
              className="absolute top-4 right-5 text-white/50 hover:text-white text-xl leading-none"
              aria-label="Close"
            >
              ×
            </button>

            <div className="text-center">
              <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
                An Act of Remembrance
              </p>
              <h3 className="mt-2 text-xl font-extralight text-white" style={{ letterSpacing: '0.05em' }}>
                Light {starName}
              </h3>
              <div
                className="mx-auto mt-4 mb-6"
                style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
              />
            </div>

            {/* Tiers */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {TIERS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTier(t.id)}
                  className="rounded-xl p-4 text-left transition-all duration-200"
                  style={{
                    background: tier === t.id ? 'rgba(229,200,131,0.12)' : 'rgba(255,255,255,0.04)',
                    border: tier === t.id ? `1px solid ${GOLD}` : '1px solid rgba(255,255,255,0.15)',
                    boxShadow: tier === t.id ? '0 0 18px rgba(255,215,140,0.2)' : 'none',
                  }}
                >
                  <p className="text-sm text-white font-light">{t.label}</p>
                  <p className="text-lg mt-0.5" style={{ color: '#ffe9b8' }}>{t.price}</p>
                  <p className="text-[10px] text-white/45 mt-1 leading-snug">{t.blurb}</p>
                </button>
              ))}
            </div>

            {/* Who is lighting it */}
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Your name</label>
            <input
              value={litBy}
              onChange={(e) => setLitBy(e.target.value)}
              maxLength={80}
              placeholder="Who is lighting this star?"
              className="w-full mb-4 pb-2 border-0 border-b bg-transparent text-white placeholder-white/30 focus:outline-none font-light"
              style={{ borderColor: 'rgba(255,255,255,0.25)' }}
            />

            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
              A dedication (optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={200}
              rows={2}
              placeholder={`e.g. Tonight, ${memorial?.firstName || 'their'} star shines for the laugh we all miss.`}
              className="w-full mb-1 p-3 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none font-light resize-none"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)' }}
            />
            <p className="text-right text-[10px] text-white/30 mb-4">{message.length}/200</p>

            {error && <p className="mb-3 text-xs text-red-300 text-center">{error}</p>}

            <button
              onClick={checkout}
              disabled={busy}
              className="w-full rounded-full bg-white hover:bg-[#ffedc4] disabled:bg-gray-700 disabled:text-gray-500 text-black py-3.5 text-xs font-medium uppercase tracking-[0.2em] transition-all duration-300 hover:shadow-[0_0_24px_rgba(255,215,140,0.35)]"
            >
              {busy ? 'One moment…' : `✦ Light it — ${TIERS.find((t) => t.id === tier)?.price}`}
            </button>
            <p className="mt-3 text-center text-white/30 text-[10px] font-light">
              Secure checkout by Stripe · Candles are always free below
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default LightStar;
